import { useEffect, useRef } from "react"

class SoundManager {
    constructor() {
        this.starter_holostoy = new Audio("/sounds/start+holostoy.mp3");
    }

    playStarter() {
        this.starter_holostoy.currentTime = 0;
        this.starter_holostoy.play();
    }

    stopStarter() {
        this.starter_holostoy.pause();
        this.starter_holostoy.currentTime = 0;
    }
}

class SoundGraphics {
    constructor() {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 256;
        this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
        this.smoothingTimeConstant = 0.8;
        this.analyser.smoothingTimeConstant = this.smoothingTimeConstant;
        this.isTestMode = false;
        this.testTime = 0;
        this.intensity = 0;
        this.maxIntensity = 3;
        this.intensityStep = 0.05;

        // Define frequency ranges for analysis
        this.frequencyRanges = {
            bass: { min: 0, max: 10 },      // 0-200 Hz
            mid: { min: 10, max: 50 },      // 200-1000 Hz
            high: { min: 50, max: 100 },    // 1000-2000 Hz
            ultra: { min: 100, max: 128 }   // 2000+ Hz
        };
    }

    async initializeContext() {
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }
    }

    connectAudioSource(audioElement) {
        try {
            const source = this.audioContext.createMediaElementSource(audioElement);
            source.connect(this.analyser);
            this.analyser.connect(this.audioContext.destination);
        } catch (error) {
            console.error('Error connecting audio source:', error);
        }
    }

    setTestMode(enabled) {
        this.isTestMode = enabled;
        this.testTime = 0;
        if (!enabled) {
            this.intensity = 0;
        }
    }

    updateIntensity(increase) {
        if (this.isTestMode) {
            if (increase) {
                this.intensity = Math.min(this.maxIntensity, this.intensity + this.intensityStep);
            } else {
                this.intensity = Math.max(0, this.intensity - this.intensityStep);
            }
        }
    }

    getFrequencyData() {
        if (this.isTestMode) {
            // Generate test frequency data
            const testData = new Uint8Array(this.analyser.frequencyBinCount);
            for (let i = 0; i < testData.length; i++) {
                // Create a wave pattern that changes over time
                const frequency = i / testData.length;
                const amplitude = Math.sin(this.testTime * 3 + frequency * Math.PI * 2) * 127 * this.intensity + 128;
                testData[i] = amplitude;
            }
            this.testTime += 0.1; // Faster wave animation
            return testData;
        }
        this.analyser.getByteFrequencyData(this.dataArray);
        return this.dataArray;
    }

    getLinearValue() {
        const data = this.getFrequencyData();
        // Calculate average of first 10 frequencies for linear visualization
        const sum = data.slice(0, 10).reduce((a, b) => a + b, 0);
        return sum / 10;
    }

    getQuadraticValue() {
        const data = this.getFrequencyData();
        // Calculate weighted average of frequencies for quadratic visualization
        // Give more weight to lower frequencies for better visualization
        const sum = data.reduce((acc, val, idx) => {
            const weight = Math.pow(1 / (idx + 1), 0.5); // Square root of inverse for smoother weighting
            return acc + (val * weight);
        }, 0);
        const weightSum = data.reduce((acc, _, idx) => acc + Math.pow(1 / (idx + 1), 0.5), 0);
        return sum / weightSum;
    }

    getTimeDomainData() {
        if (this.isTestMode) {
            // Generate test time domain data
            const timeData = new Uint8Array(this.analyser.frequencyBinCount);
            for (let i = 0; i < timeData.length; i++) {
                // Create a wave pattern that changes over time
                const time = i / timeData.length;
                const amplitude = Math.sin(this.testTime * 3 + time * Math.PI * 2) * 127 * this.intensity + 128;
                timeData[i] = amplitude;
            }
            return timeData;
        }
        const timeData = new Uint8Array(this.analyser.frequencyBinCount);
        this.analyser.getByteTimeDomainData(timeData);
        return timeData;
    }

    getFrequencyRangeData(range) {
        const data = this.getFrequencyData();
        const { min, max } = this.frequencyRanges[range];
        const rangeData = data.slice(min, max);
        return {
            average: rangeData.reduce((a, b) => a + b, 0) / rangeData.length,
            max: Math.max(...rangeData),
            min: Math.min(...rangeData)
        };
    }

    getVolume() {
        const data = this.getTimeDomainData();
        // Calculate RMS (Root Mean Square) value for volume
        const rms = Math.sqrt(data.reduce((acc, val) => acc + Math.pow(val - 128, 2), 0) / data.length);
        return rms;
    }

    getAllFrequencyData() {
        return {
            bass: this.getFrequencyRangeData('bass'),
            mid: this.getFrequencyRangeData('mid'),
            high: this.getFrequencyRangeData('high'),
            ultra: this.getFrequencyRangeData('ultra'),
            volume: this.getVolume()
        };
    }
}

export default function Sound({ isEngineOn, onGraphicsCreated }) {
    const soundRef = useRef(null);
    const graphicsRef = useRef(null);
    const sourceConnectedRef = useRef(false);

    useEffect(() => {
        // Initialize Sound instance
        soundRef.current = new SoundManager();
        graphicsRef.current = new SoundGraphics();

        // Expose graphics instance to parent
        onGraphicsCreated?.(graphicsRef.current);

        // Cleanup function to stop audio when component unmounts
        return () => {
            if (soundRef.current) {
                soundRef.current.stopStarter();
            }
        };
    }, [onGraphicsCreated]);

    useEffect(() => {
        const handleAudioSetup = async () => {
            if (!soundRef.current || !graphicsRef.current) return;

            try {
                // Resume audio context
                await graphicsRef.current.initializeContext();

                // Connect audio source if not already connected
                if (!sourceConnectedRef.current && soundRef.current.starter_holostoy) {
                    graphicsRef.current.connectAudioSource(soundRef.current.starter_holostoy);
                    sourceConnectedRef.current = true;
                }

                // Play or stop the sound
                if (isEngineOn) {
                    await soundRef.current.starter_holostoy.play().catch(error => {
                        console.error('Error playing audio:', error);
                    });
                } else {
                    soundRef.current.stopStarter();
                }
            } catch (error) {
                console.error('Error in audio setup:', error);
            }
        };

        handleAudioSetup();
    }, [isEngineOn]);

    return null; // This component doesn't render anything
}